import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TablePagination,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from '@mui/material';

const AuditLogsTable = ({ data = [], loading = false, isPayrollProcessing = false }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format timestamp to IST (Indian Standard Time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      // Convert to IST (Asia/Kolkata timezone)
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return timestamp;
    }
  };

  // Get action color
  const getActionColor = (action) => {
    switch (action) {
      case 'LOGIN':
        return 'success';
      case 'LOGOUT':
        return 'default';
      case 'UPDATE':
        return 'warning';
      case 'CREATE':
        return 'primary';
      case 'DELETE':
        return 'error';
      case 'OTP_SEND':
        return 'info';
      default:
        return 'default';
    }
  };

  const paginatedData = useMemo(() => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [data, page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        {isPayrollProcessing ? 'No payroll processing logs found.' : 'No audit logs found.'}
      </Typography>
    );
  }

  // Render payroll processing logs table
  if (isPayrollProcessing) {
    return (
      <div>
        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#212529', color: 'white' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Employee IDs</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Month</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Year</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Net Amount Paid</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Timestamp (IST)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((log, index) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ textAlign: 'center' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {log.employee_ids && Array.isArray(log.employee_ids) && log.employee_ids.length > 0
                      ? log.employee_ids.join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip
                      label={log.action || '-'}
                      color={getActionColor(log.action)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.module || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.month || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.year || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {log.net_amount_paid !== null && log.net_amount_paid !== undefined
                      ? Number(log.net_amount_paid).toLocaleString('en-IN')
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.description || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{formatTimestamp(log.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #dee2e6' }}
        />
      </div>
    );
  }

  // Render audit logs table (no fixed columns, all scrollable)
  return (
    <div>
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#212529', color: 'white' }}>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Sr. No.</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>User Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Record ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Fields Names</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Old Values</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>New Values</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Timestamp (IST)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((log, index) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ textAlign: 'center' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.employee_id || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.user_name || localStorage.getItem('activeUserName') || '-'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{log.record_id || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Chip
                    label={log.action || '-'}
                    color={getActionColor(log.action)}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{log.module || '-'}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {log.fields_names && log.fields_names.length > 0
                    ? log.fields_names.join(', ')
                    : '-'}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Box
                    component="pre"
                    sx={{
                      margin: '0 auto',
                      padding: 0.5,
                      backgroundColor: '#ffebee',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxWidth: '200px',
                      maxHeight: '100px',
                      overflow: 'auto',
                      textAlign: 'left',
                    }}
                  >
                    {log.field_old_value && Object.keys(log.field_old_value).length > 0
                      ? JSON.stringify(log.field_old_value, null, 2)
                      : '-'}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Box
                    component="pre"
                    sx={{
                      margin: '0 auto',
                      padding: 0.5,
                      backgroundColor: '#e8f5e9',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxWidth: '200px',
                      maxHeight: '100px',
                      overflow: 'auto',
                      textAlign: 'left',
                    }}
                  >
                    {log.field_new_value && Object.keys(log.field_new_value).length > 0
                      ? JSON.stringify(log.field_new_value, null, 2)
                      : '-'}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{formatTimestamp(log.timestamp)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ borderTop: '1px solid #dee2e6' }}
      />
    </div>
  );
};

export default AuditLogsTable;
